import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import NewJobPricelistPowerPoleQuestionsData from './NewJobPricelistPowerPoleQuestions';
import { GetQuery, PostQuery } from './Shared';

interface JobQuoteQuestionsProps {
    jobId: number;
    itemId: number;
    showModal: boolean;
    readOnly: boolean;
    yesNoOptions: GenericList[];
    hideModal(): void;
    saveModal(): void;
}

interface GenericList {
    id: number;
    name: string;
}

interface PowerPoleQuestions {
    isUnderground: boolean;
    superSafeConnecting: number;
    superSafeSupplierId: number;
    powerPoleLocationId: number;
    powerPreviouslyConnected: number;
    powerAbolished: number;
    siteLocked: number;
    clearSightLine: number;
    pitOnSite: number;
    pitDistanceId: number;
    superSafeDiggingTrench: number;
}


const JobQuoteQuestionsData = (props: JobQuoteQuestionsProps) => {
    const [loading, setLoading] = React.useState(true);
    const [powerPoleQuestions, setPowerPoleQuestions] = React.useState<PowerPoleQuestions>({
        isUnderground: false,
        superSafeConnecting: -1,
        superSafeSupplierId: -1,
        powerPoleLocationId: -1,
        powerPreviouslyConnected: -1,
        powerAbolished: -1,
        siteLocked: -1,
        clearSightLine: -1,
        pitOnSite: -1,
        pitDistanceId: -1,
        superSafeDiggingTrench: -1
    });
    const [isUnderground, setIsUnderground] = React.useState(false);
    const [gateCode, setGateCode] = React.useState("");
    const [superSafeSupplierOptions, setSuperSafeSupplierOptions] = React.useState<GenericList[]>([]);
    const [powerPoleLocationOptions, setPowerPoleLocationOptions] = React.useState<GenericList[]>([]);
    const [powerPoleDistanceOptions, setPowerPoleDistanceOptions] = React.useState<GenericList[]>([]);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        if (props.itemId > 0) {
            getData();
        }
    }, [props.itemId]);

    const getData = async () => {
        const apiUrl = 'GetPowerPoleQuestions?JobId=' + props.jobId + '&itemId=' + props.itemId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setPowerPoleLocationOptions(result.data.powerPoleLocationOptions);
            setPowerPoleDistanceOptions(result.data.powerPoleDistanceOptions);
            setSuperSafeSupplierOptions(result.data.superSafeSupplierOptions);
            setGateCode(result.data.gateCode);
            setIsUnderground(result.data.isUnderground);
            setPowerPoleQuestions(result.data.powerPoleQuestions);
            setLoading(false);
        }
    }

    const resetQuestions = () => {
        var questions: PowerPoleQuestions = {
            isUnderground: false,
            superSafeConnecting: -1,
            superSafeSupplierId: -1,
            powerPoleLocationId: -1,
            powerPreviouslyConnected: -1,
            powerAbolished: -1,
            siteLocked: -1,
            clearSightLine: -1,
            pitOnSite: -1,
            pitDistanceId: -1,
            superSafeDiggingTrench: -1
        };

        return questions;
    }


    const hideModal = () => {
        var resetQues = resetQuestions();
        setPowerPoleQuestions(resetQues);
        setIsUnderground(false);
        setGateCode("");
        props.hideModal();
    }

    const updateGateCode = (updatedCode: string) => {
        setGateCode(updatedCode);
    }

    const updatePowerPoleQuestions = (updatedQuestions: PowerPoleQuestions) => {
        setPowerPoleQuestions(prevState => ({...prevState, updatedQuestions}));
    }

    const validate = () => {
        var errorList: { [key: string]: string } = {};
        var formIsValid = true;

        //power pole validation
        if (powerPoleQuestions.superSafeConnecting < 0) {
            formIsValid = false;
            errorList["superSafeConnecting"] = "Super Safe Connecting Electricity is required";
        }

        if (powerPoleQuestions.superSafeConnecting === 1 && powerPoleQuestions.superSafeSupplierId < 0) {
            formIsValid = false;
            errorList["superSafeSupplierId"] = "Distributor is required";
        }

        if (powerPoleQuestions.powerPoleLocationId < 0) {
            formIsValid = false;
            errorList["powerPoleLocationId"] = "Power Pole Location is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected < 0) {
            formIsValid = false;
            errorList["powerPreviouslyConnected"] = "Power to the property is required";
        }

        if (powerPoleQuestions.powerPreviouslyConnected === 1 && powerPoleQuestions.powerAbolished < 0) {
            formIsValid = false;
            errorList["powerAbolished"] = "Power Abolished is required";
        }

        if (powerPoleQuestions.siteLocked < 0) {
            formIsValid = false;
            errorList["siteLocked"] = "Is Site Locked is required";
        }

        if (!isUnderground && powerPoleQuestions.clearSightLine < 0) {
            formIsValid = false;
            errorList["clearSightLine"] = "Clear Line of Sight is required";
        }

        if (isUnderground) {
            if (powerPoleQuestions.pitOnSite < 0) {
                formIsValid = false;
                errorList["pitOnSite"] = "Pit on site is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.pitDistanceId < 0) {
                formIsValid = false;
                errorList["pitDistanceId"] = "Distance between pit and pole is required";
            } else if (powerPoleQuestions.pitOnSite === 1 && powerPoleQuestions.superSafeDiggingTrench < 0) {
                formIsValid = false;
                errorList["superSafeDiggingTrench"] = "Super Safe digging trench is required";
            }
        }
        setErrors(errorList);
 
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveQuestions();
        } else {
            toast.error("Please fix validation errors before Saving!");
        }
    }

    const saveQuestions = async () => {
        var saveQuestions = {
            jobId: props.jobId,
            gateCode: gateCode,
            powerPoleQuestions: powerPoleQuestions
        };

        var jsonItem = JSON.stringify(saveQuestions);

        const apiUrl = "SavePowerPoleQuestions";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            //let data = await resp.json();
            saveModal();
        }  
    }

    const saveModal = () => {
        var resetQues = resetQuestions();
        setPowerPoleQuestions(resetQues);
        setIsUnderground(false);
        setGateCode("");
        props.saveModal();
    }

    const ppQuestions = <NewJobPricelistPowerPoleQuestionsData isReadOnly={props.readOnly} isUnderground={isUnderground} powerPoleQuestions={powerPoleQuestions} gateCode={gateCode} yesNoOptions={props.yesNoOptions} powerPoleLocationOptions={powerPoleLocationOptions} powerPoleDistanceOptions={powerPoleDistanceOptions} superSafeSupplierOptions={superSafeSupplierOptions} errors={errors} updateGateCode={updateGateCode} updatePowerPoleQuestions={updatePowerPoleQuestions} />

    return (
        <Modal show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Power Pole Questions</Modal.Title>
                <button type="button" className="close" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                {loading ? <div>Loading...</div> : ppQuestions}
            </Modal.Body>
            <Modal.Footer>
                <button className={props.readOnly ? "hidden" : "popup-button--yes"} onClick={save}>Save</button>
                <button className={props.readOnly ? "popup-button--yes" : "hidden"} onClick={hideModal}>Close</button>
            </Modal.Footer>
        </Modal>
    )
}

export default JobQuoteQuestionsData;

